import { ApolloClient } from '@apollo/client';
import { withApollo } from '@apollo/client/react/hoc';
import {
  getFrontendConfigValue,
  LanguageSelector,
  MenuItems,
  PlainButton,
  theme,
  UserMenuDropdown,
} from '@arnold/common';
import styled from '@emotion/styled/macro';
import compose from 'lodash.flowright';
import { useState } from 'react';
import { Navbar as NavbatStrap } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router';
import { SideCollapse, SideShow } from '../../assets/images';
import { IsAccessTokenValidQuery } from '../../generated/hooks';
import auth from '../../lib/auth';
import { allowedLanguages, handleLanguageChange } from '../../lib/common';
import { cookieConsentManager } from '../CookieConsent';

const LanguageSelectorContainer = styled.div`
  margin-top: -3px;
`;

const ToggleSideBar = styled.img`
  max-height: 128px;
  object-fit: contain;
  margin-right: ${theme.spacing.g};
`;

const MobileToggleSidebar = styled(PlainButton)`
  background: transparent !important;
  @media screen and (min-width: 992px) {
    display: none;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.d};
`;

interface IProps extends RouteComponentProps<any> {
  user: NonNullable<IsAccessTokenValidQuery['isAccessTokenValid']['user']>;
  client: ApolloClient<any>;
}

function Navbar(props: IProps) {
  const [t, i18n] = useTranslation('Navbar');
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const { user } = props;

  const toggleSidebarVisibility = () => {
    document.documentElement.classList.toggle('nav-open');
    setIsSidebarVisible((prevValue) => !prevValue);
  };

  const handleSignOut = async () => {
    const shouldRedirectToLogin = await auth.logout();
    if (shouldRedirectToLogin) {
      props.history.push(`/login/${i18n.language}`);
    }
  };

  const handleCookieSettingsClick = () => {
    // @ts-ignore
    cookieConsentManager?.showPreferences();
  };

  const handleClickOnAcademy = () => {
    window.open('https://intercom.help/arnold-robot/cs', '_blank')?.focus();
  };

  const handleClickOnMyArnold = () => {
    window.location.href = `${getFrontendConfigValue('ARNOLD_URL')}/overview`;
  };

  const name = `${user.firstname} ${user.surname}`;
  return (
    <NavbatStrap className={`navbar-absolute fixed-top navbar-transparent`} expand="lg">
      <div className="container-fluid">
        <div className="navbar-wrapper">
          <MobileToggleSidebar onClick={toggleSidebarVisibility}>
            <ToggleSideBar src={isSidebarVisible ? SideCollapse : SideShow} />
          </MobileToggleSidebar>
        </div>
        <Nav>
          <LanguageSelectorContainer>
            <LanguageSelector onChange={handleLanguageChange} allowedLanguages={[...allowedLanguages]} />
          </LanguageSelectorContainer>
          <UserMenuDropdown
            buttonTitle={[user.firstname, user.surname].filter(Boolean).map((n) => (n ? n.charAt(0) : null))}
            title={name}
            showMenuHeader={true}
            isLogged={true}
            menuItems={
              [
                {
                  title: t('myArnold'),
                  onClick: handleClickOnMyArnold,
                  dataIcom: 'user-menu-my-arnold-item',
                  element: 'a',
                },
                ['cs', 'sk'].includes(i18n.language) && {
                  title: t('arnoldAcademy'),
                  onClick: handleClickOnAcademy,
                  element: 'a',
                },
                {
                  title: t('cookieSettings'),
                  onClick: handleCookieSettingsClick,
                  dataIcom: 'user-menu-cookie-settings-item',
                  element: 'button',
                },
              ].filter(Boolean) as MenuItems
            }
            showPrimaryActionInFooter={false}
            showFooter={true}
            primaryAction={null}
            footerMenuItem={{
              title: t('signOut'),
              onClick: handleSignOut,
              dataIcom: 'user-menu-sign-out-item',
              element: 'button',
            }}
          />
        </Nav>
      </div>
    </NavbatStrap>
  );
}

export default compose(withApollo, withRouter)(Navbar);
